import { getApiBaseUrl } from './api-config';
import { ApiError } from './api-error';
import { recordApiFailure } from '../diagnostics/diagnostics-store';

export type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Multipart-тело (например скриншот баг-репорта); Content-Type выставляет браузер. */
  bodyFormData?: FormData;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  skipAuth?: boolean;
  /** Не выполнять цепочку refresh+retry при 401 (редкие случаи вне обычного staff API). */
  skipAuthRefresh?: boolean;
  impersonateOrganizationId?: number | null;
  /**
   * Bearer для этого запроса вместо `getAccessToken()` (загрузка `/auth/me` до коммита access в session).
   */
  accessTokenOverride?: string;
};

export type ApiClientAuth = {
  getAccessToken: () => string | null;
  getImpersonateOrganizationId: () => number | null;
  getRefreshToken: () => string | null;
  applySessionTokens: (accessToken: string, refreshToken: string | null) => void;
  clearSessionAfterFailedRefresh: () => void;
};

function joinUrl(base: string, path: string): string {
  if (path.startsWith('http')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

function isAuthRefreshPath(path: string): boolean {
  const normalized = path.split('?')[0]?.replace(/\/+$/, '') ?? '';
  return normalized === '/auth/refresh' || normalized.endsWith('/auth/refresh');
}

let refreshInFlight: Promise<boolean> | null = null;

/** Сброс single-flight между unit-тестами (Vitest). */
export function resetAuthRefreshSingleFlightForTests(): void {
  refreshInFlight = null;
}

function enqueueRefresh(auth: ApiClientAuth, base: string): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    try {
      const rt = auth.getRefreshToken();
      if (!rt) return false;
      const url = joinUrl(base, '/auth/refresh');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: rt })
      });
      const ct = res.headers.get('content-type') ?? '';
      const raw = await res.text();
      if (!res.ok) {
        return false;
      }
      if (!ct.includes('application/json') || !raw.trim()) {
        return false;
      }
      let data: unknown;
      try {
        data = JSON.parse(raw) as unknown;
      } catch {
        return false;
      }
      const o = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : null;
      const access = typeof o?.access_token === 'string' ? o.access_token : '';
      if (!access) {
        return false;
      }
      const nextRt =
        o?.refresh_token === null || o?.refresh_token === undefined
          ? null
          : typeof o.refresh_token === 'string'
            ? o.refresh_token
            : null;
      auth.applySessionTokens(access, nextRt);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function shouldTryRefreshOn401(path: string, options: ApiRequestOptions, hadAccessToken: boolean): boolean {
  if (!hadAccessToken) return false;
  if (options.skipAuth) return false;
  if (options.skipAuthRefresh) return false;
  if (isAuthRefreshPath(path)) return false;
  return true;
}

function parseNonNegativeInt(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) return Math.trunc(raw);
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw.trim());
    if (Number.isFinite(n) && n >= 0) return Math.trunc(n);
  }
  return null;
}

/**
 * Явное поле total в JSON-объекте пагинированного ответа (не длина массива).
 * Для ответа-массива без обёртки возвращает null — там total задаётся заголовком или длиной страницы.
 */
function explicitTotalFromPagedJsonBody(parsedBody: unknown): number | null {
  if (parsedBody == null || Array.isArray(parsedBody) || typeof parsedBody !== 'object') return null;
  const o = parsedBody as Record<string, unknown>;
  const nested =
    o.data && typeof o.data === 'object' && !Array.isArray(o.data)
      ? (o.data as Record<string, unknown>)
      : null;
  const meta =
    o.meta && typeof o.meta === 'object' && !Array.isArray(o.meta) ? (o.meta as Record<string, unknown>) : null;
  const rawT = o.total ?? o.Total ?? nested?.total ?? nested?.Total ?? meta?.total ?? meta?.Total;
  return parseNonNegativeInt(rawT);
}

export function inferListTotalFromJsonBody(parsedBody: unknown): number | null {
  if (parsedBody == null) return null;
  const fromObject = explicitTotalFromPagedJsonBody(parsedBody);
  if (fromObject != null) return fromObject;
  if (Array.isArray(parsedBody)) return parsedBody.length;
  return null;
}

export function createApiClient(auth: ApiClientAuth) {
  async function performFetch(path: string, options: ApiRequestOptions = {}): Promise<Response> {
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const base = getApiBaseUrl();
    const url = joinUrl(base, path);

    const hadAccessToken = Boolean(auth.getAccessToken());

    const doFetch = async (): Promise<Response> => {
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...options.headers
      };
      if (!options.skipAuth) {
        const token =
          typeof options.accessTokenOverride === 'string' && options.accessTokenOverride.length > 0
            ? options.accessTokenOverride
            : auth.getAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const imp =
        options.impersonateOrganizationId === undefined
          ? auth.getImpersonateOrganizationId()
          : options.impersonateOrganizationId;
      if (imp != null && imp > 0) {
        headers['X-Impersonate-Organization-Id'] = String(imp);
      }
      let bodyInit: BodyInit | undefined;
      if (options.bodyFormData !== undefined) {
        bodyInit = options.bodyFormData;
      } else if (options.body !== undefined) {
        headers['Content-Type'] = 'application/json';
        bodyInit = JSON.stringify(options.body);
      }
      try {
        return await fetch(url, {
          method: options.method ?? 'GET',
          headers,
          body: bodyInit,
          signal: options.signal
        });
      } catch {
        recordApiFailure({
          method: options.method ?? 'GET',
          path,
          status: 0,
          message: 'Сеть недоступна или запрос прерван',
          durationMs: (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt
        });
        throw new ApiError(0, 'Сеть недоступна или запрос прерван');
      }
    };

    let response = await doFetch();

    if (
      response.status === 401 &&
      shouldTryRefreshOn401(path, options, hadAccessToken) &&
      auth.getRefreshToken()
    ) {
      const refreshed = await enqueueRefresh(auth, base);
      if (!refreshed) {
        auth.clearSessionAfterFailedRefresh();
      } else {
        response = await doFetch();
        if (response.status === 401) {
          auth.clearSessionAfterFailedRefresh();
        }
      }
    } else if (
      response.status === 401 &&
      shouldTryRefreshOn401(path, options, hadAccessToken) &&
      !auth.getRefreshToken()
    ) {
      auth.clearSessionAfterFailedRefresh();
    }

    return response;
  }

  /** Единая обработка ошибок ответа: 401 context_revoked — выход и экран входа с сообщением. */
  function throwResponseError(status: number, raw: string, ct: string, path = '', startedAt = 0, method = 'GET', requestId: string | null = null): never {
    const err = ApiError.fromResponse(status, raw, ct);
    recordApiFailure({
      method,
      path,
      status,
      code: err.errorCode,
      message: err.detail,
      requestId,
      durationMs: startedAt ? (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt : 0
    });
    if (err.status === 401 && err.errorType === 'context_revoked') {
      auth.clearSessionAfterFailedRefresh();
      if (typeof window !== 'undefined') {
        window.location.assign('/login?context_revoked=1');
      }
    }
    throw err;
  }

  async function request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const response = await performFetch(path, options);
    const ct = response.headers.get('content-type') ?? '';
    const raw = await response.text();
    if (response.ok) {
      if (response.status === 204 || response.status === 205 || !raw.trim()) {
        return undefined as T;
      }
      if (!ct.includes('application/json')) {
        return raw as T;
      }
      return JSON.parse(raw) as T;
    }
    return throwResponseError(response.status, raw, ct, path, startedAt, options.method ?? 'GET', response.headers.get('X-Request-Id'));
  }

  /**
   * Успешный JSON + total для постраничных списков API v1.
   * Если в теле JSON-объекта есть явное поле `total` (или вложенное `data` / `meta`) — оно приоритетнее заголовка
   * `X-Total-Count` (прокси/CORS и промежуточные слои иногда подставляют в заголовок размер страницы).
   * Иначе: заголовок `X-Total-Count`, затем эвристика `inferListTotalFromJsonBody` (в т.ч. длина массива-ответа).
   */
  async function requestJsonWithTotalCount<T>(
    path: string,
    options: ApiRequestOptions = {}
  ): Promise<{ data: T; total: number }> {
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const response = await performFetch(path, options);
    const ct = response.headers.get('content-type') ?? '';
    const raw = await response.text();
    if (response.ok) {
      if (response.status === 204 || response.status === 205 || !raw.trim()) {
        return { data: undefined as T, total: 0 };
      }
      if (!ct.includes('application/json')) {
        return { data: raw as T, total: 0 };
      }
      const parsedBody = JSON.parse(raw) as unknown;
      const explicitBody = explicitTotalFromPagedJsonBody(parsedBody);
      if (explicitBody != null) {
        return { data: parsedBody as T, total: explicitBody };
      }
      const totalHdr = response.headers.get('X-Total-Count');
      const hdrParsed = totalHdr != null && totalHdr !== '' ? Number.parseInt(totalHdr, 10) : Number.NaN;
      if (Number.isFinite(hdrParsed) && hdrParsed >= 0) {
        return { data: parsedBody as T, total: hdrParsed };
      }
      const fromBody = inferListTotalFromJsonBody(parsedBody);
      if (fromBody != null) {
        return { data: parsedBody as T, total: fromBody };
      }
      return { data: parsedBody as T, total: 0 };
    }
    return throwResponseError(response.status, raw, ct, path, startedAt, options.method ?? 'GET', response.headers.get('X-Request-Id'));
  }

  return { request, requestJsonWithTotalCount };
}

export type ApiClient = ReturnType<typeof createApiClient>;
